import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';

import { VideoSceneResponse, VideoSceneUpdateRequest, VideoSceneDataBaseRequest } from '@solverml/api';
import { PlayModelOptions } from 'src/app/features/video/controller/video-scene.service';
import {
  findUnknownPlayModelActions,
  normalizePlayModelInput,
  PLAY_MODEL_DISPLAY_LIMIT,
  PLAY_MODEL_LABEL,
  PLAY_MODEL_VALIDATION_REGEX,
} from './play-model-reference';
import { PlayModelHelpModalComponent } from './play-model-help-modal.component';

@Component({
  selector: 'app-scene-table',
  templateUrl: './scene-table.component.html',
  styleUrls: ['./scene-table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule
  ]
})
export class SceneTableComponent implements OnChanges {

  @Input() scenes: VideoSceneResponse[] = [];
  @Input() currentScene: VideoSceneResponse | null = null;
  @Input() playModelOptions: PlayModelOptions = {};

  @Output() onSceneSave = new EventEmitter<VideoSceneUpdateRequest>();
  @Output() onSceneDelete = new EventEmitter<VideoSceneResponse>();
  @Output() onEditingStateChange = new EventEmitter<boolean>();

  public dynamicColumns: string[] = [];
  private ignoredColumns = ['Asistencia', 'Goleador', 'Tiempo'];
  readonly playModelLabel = PLAY_MODEL_LABEL;
  readonly playModelDisplayLimit = PLAY_MODEL_DISPLAY_LIMIT;

  public visibleScenes: VideoSceneResponse[] = [];

  public editingSceneId: string | null = null;
  public editForm: FormGroup | null = null;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['playModelOptions'] && this.playModelOptions) {
      this.dynamicColumns = Object.keys(this.playModelOptions)
        .filter(key => !this.ignoredColumns.includes(key));
    }

    if (changes['scenes'] || changes['currentScene']) {
      this.calculateVisibleScenes();
    }
  }

  private calculateVisibleScenes() {
    if (!this.scenes || this.scenes.length === 0) {
      this.visibleScenes = [];
      return;
    }

    const totalScenes = this.scenes.length;
    if (totalScenes <= 3) {
      this.visibleScenes = this.scenes;
      return;
    }

    const currentIndex = this.currentScene
      ? this.scenes.findIndex(s => s.id === this.currentScene!.id)
      : -1;

    let start: number;

    if (currentIndex === -1) {
      start = 0;
    } else if (currentIndex === 0) {
      start = 0;
    } else if (currentIndex >= totalScenes - 1) {
      start = totalScenes - 3;
    } else {
      start = currentIndex - 1;
    }

    start = Math.max(0, Math.min(start, totalScenes - 3));
    this.visibleScenes = this.scenes.slice(start, start + 3);
  }

  formatTime(seconds: number): string {
    if (seconds === null || seconds === undefined) return '-';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  getTeamDisplayName(scene: VideoSceneResponse, side: 'attacking' | 'defending'): string {
    const assignedName = side === 'attacking'
      ? scene.attacking_team_name
      : scene.defending_team_name;

    if (assignedName) {
      return assignedName;
    }

    const defaultTeamId = side === 'attacking'
      ? scene.default_attacking_team_id
      : scene.default_defending_team_id;

    if (defaultTeamId === 1 || defaultTeamId === 2) {
      return `Equipo ${defaultTeamId}`;
    }

    return '-';
  }

  getTeamTooltip(scene: VideoSceneResponse, side: 'attacking' | 'defending'): string {
    const assignedName = side === 'attacking'
      ? scene.attacking_team_name
      : scene.defending_team_name;

    if (assignedName) {
      return assignedName;
    }

    const defaultTeamId = side === 'attacking'
      ? scene.default_attacking_team_id
      : scene.default_defending_team_id;

    if (defaultTeamId === 1 || defaultTeamId === 2) {
      return `Equipo ${defaultTeamId} por defecto`;
    }

    return 'Sin equipos asignados';
  }

  getDisplayValue(scene: VideoSceneResponse, columnKey: string): string {
    const dataItem = scene.video_scene_data.find(d => d.label === columnKey);
    return dataItem?.value || '-';
  }

  getRenderedValue(scene: VideoSceneResponse, columnKey: string): string {
    const value = this.getDisplayValue(scene, columnKey);
    if (!this.isPlayModelColumn(columnKey) || value === '-' || value.length <= this.playModelDisplayLimit) {
      return value;
    }

    return `${value.slice(0, this.playModelDisplayLimit - 3)}...`;
  }

  getCellTooltip(scene: VideoSceneResponse, columnKey: string): string {
    if (!this.isPlayModelColumn(columnKey)) {
      return '';
    }

    const value = this.getDisplayValue(scene, columnKey);
    return value === '-' ? '' : value;
  }

  isPlayModelColumn(columnKey: string): boolean {
    return columnKey === this.playModelLabel;
  }

  startEditing(scene: VideoSceneResponse, event: MouseEvent) {
    event.stopPropagation();

    if (scene.id !== this.currentScene?.id) return;

    this.editingSceneId = scene.id;
    this.buildEditForm(scene);
    this.onEditingStateChange.emit(true);
  }

  cancelEditing() {
    this.editingSceneId = null;
    this.editForm = null;
    this.onEditingStateChange.emit(false);
  }

  saveEditing(scene: VideoSceneResponse, event: MouseEvent) {
    event.stopPropagation();

    if (!this.editForm || this.editForm.invalid) return;

    const formValue = this.editForm.value;

    const updateRequest: VideoSceneUpdateRequest = {
      id: scene.id,
      video_id: scene.video_id,
      label: scene.label,
      start_time_in_seconds: scene.start_time_in_seconds,
      end_time_in_seconds: scene.end_time_in_seconds,
      analyzable: scene.analyzable,
      video_scene_data: []
    };

    const newData: VideoSceneDataBaseRequest[] = [];
    for (const colKey of this.dynamicColumns) {
      const existingData = scene.video_scene_data.find(d => d.label === colKey);

      if (this.isPlayModelColumn(colKey)) {
        const playModelValue = normalizePlayModelInput(formValue[colKey]);
        if (!playModelValue) {
          continue;
        }

        const playDefinitionId = this.resolvePlayModelDefinitionId(existingData, playModelValue);
        if (playDefinitionId !== null) {
          newData.push({
            label: colKey,
            play_definition_id: playDefinitionId,
            value: playModelValue
          });
        }
        continue;
      }

      const selectedOptionId = formValue[colKey];
      if (selectedOptionId) {
        const optionsList = this.playModelOptions[colKey];
        const selectedOption = optionsList?.find(opt => opt.id === selectedOptionId);

        if (selectedOption) {
          newData.push({
            label: colKey,
            play_definition_id: selectedOptionId,
            value: selectedOption.display || ''
          });
        }
      }
    }
    updateRequest.video_scene_data = newData;

    this.onSceneSave.emit(updateRequest);
    this.cancelEditing();
  }

  private buildEditForm(scene: VideoSceneResponse) {
    const group: any = {};
    for (const colKey of this.dynamicColumns) {
      const existingData = scene.video_scene_data.find(d => d.label === colKey);
      if (this.isPlayModelColumn(colKey)) {
        group[colKey] = [
          existingData?.value || '',
          [this.playModelValidator()]
        ];
      } else {
        group[colKey] = [existingData?.play_definition_id || null];
      }
    }
    this.editForm = this.fb.group(group);
  }

  openPlayModelHelp(event: MouseEvent): void {
    event.stopPropagation();
    this.dialog.open(PlayModelHelpModalComponent, {
      width: '760px',
      maxWidth: '95vw'
    });
  }

  hasPlayModelFormatError(controlName: string): boolean {
    const control = this.getEditControl(controlName);
    return !!control?.hasError('playModelFormat');
  }

  getPlayModelSoftWarning(controlName: string): string | null {
    const control = this.getEditControl(controlName);
    if (!control) {
      return null;
    }

    const normalizedValue = normalizePlayModelInput(control.value);
    if (!normalizedValue || control.hasError('playModelFormat')) {
      return null;
    }

    const unknownActions = findUnknownPlayModelActions(normalizedValue);
    if (!unknownActions.length) {
      return null;
    }

    return `Acciones no reconocidas: ${unknownActions.join(', ')}. Se permitirá guardar igualmente.`;
  }

  deleteScene(scene: VideoSceneResponse, event: MouseEvent) {
    event.stopPropagation();
    if (scene.id !== this.currentScene?.id) return;

    this.onSceneDelete.emit(scene);
  }

  private getEditControl(controlName: string): AbstractControl | null {
    return this.editForm?.get(controlName) || null;
  }

  private playModelValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const normalizedValue = normalizePlayModelInput(control.value);
      if (!normalizedValue) {
        return null;
      }

      return (PLAY_MODEL_VALIDATION_REGEX.test(normalizedValue) || this.isKnownLegacyPlayModelValue(normalizedValue))
        ? null
        : { playModelFormat: true };
    };
  }

  private resolvePlayModelDefinitionId(
    existingData: VideoSceneResponse['video_scene_data'][number] | undefined,
    playModelValue: string
  ): number | null {
    const normalizedExistingValue = normalizePlayModelInput(existingData?.value);
    if (existingData?.category === 'modelo_juego' && normalizedExistingValue === playModelValue) {
      return existingData.play_definition_id;
    }

    const matchingLegacyOptionId = this.getMatchingLegacyPlayModelOptionId(playModelValue);
    if (matchingLegacyOptionId !== null) {
      return matchingLegacyOptionId;
    }

    return this.getPlayModelAnchorDefinitionId();
  }

  private getPlayModelAnchorDefinitionId(): number | null {
    const modelOptions = this.playModelOptions[this.playModelLabel] || [];
    const anchorOption = modelOptions.find(option => option.display === null);
    return anchorOption?.id ?? null;
  }

  private getMatchingLegacyPlayModelOptionId(playModelValue: string): number | null {
    const modelOptions = this.playModelOptions[this.playModelLabel] || [];
    const matchingOption = modelOptions.find(option =>
      option.display !== null && normalizePlayModelInput(option.display) === playModelValue
    );

    return matchingOption?.id ?? null;
  }

  private isKnownLegacyPlayModelValue(playModelValue: string): boolean {
    return this.getMatchingLegacyPlayModelOptionId(playModelValue) !== null;
  }
}
